import { useState, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function FlipCardsSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'grid';
    const cols = config.columns || 3;
    const gridCls = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    /* ── tilt-3d ── */
    if (variant === 'tilt-3d') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                        {items.map((item, i) => (
                            <TiltCard key={i} item={item} i={i} s={s} visible={visible} />
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── minimal ── */
    if (variant === 'minimal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i}
                                className={`group relative rounded-2xl p-6 text-center transition-all duration-500 cursor-pointer ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{
                                    transitionDelay: visible ? `${150 + i * 100}ms` : '0ms',
                                    backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f9fafb',
                                }}>
                                {/* Front content — fades out on hover */}
                                <div className="transition-opacity duration-500 group-hover:opacity-0">
                                    <span className="text-4xl mb-4 block">{item.front_icon || '⭐'}</span>
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.front_title}</h3>
                                    {item.front_subtitle && <p className="text-sm mt-2" style={{ color: s.textSub }}>{item.front_subtitle}</p>}
                                </div>
                                {/* Back content — fades in on hover */}
                                <div className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center p-6 text-center opacity-0 group-hover:opacity-100 transition-all duration-500"
                                    style={{ backgroundColor: s.accent }}>
                                    <h4 className="text-lg font-bold mb-2 text-white">{item.back_title || item.front_title}</h4>
                                    <p className="text-sm text-white/90 leading-relaxed">{item.back_text}</p>
                                    {item.back_cta && (
                                        <a {...resolveLinkProps(item.back_link || '#contact')} className="mt-4 px-5 py-2 rounded-lg bg-white/20 text-sm font-semibold text-white hover:bg-white/30 transition-colors">
                                            {item.back_cta}
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── grid (default — 3D flip) ── */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                    {items.map((item, i) => (
                        <div key={i} className={`group [perspective:1000px] h-64 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                            <div className="relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                                {/* Front */}
                                <div className="absolute inset-0 [backface-visibility:hidden] rounded-2xl flex flex-col items-center justify-center p-6 text-center shadow-lg"
                                    style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f9fafb' }}>
                                    <span className="text-4xl mb-4">{item.front_icon || '⭐'}</span>
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.front_title}</h3>
                                    {item.front_subtitle && <p className="text-sm mt-2" style={{ color: s.textSub }}>{item.front_subtitle}</p>}
                                </div>
                                {/* Back */}
                                <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl flex flex-col items-center justify-center p-6 text-center text-white"
                                    style={{ backgroundColor: s.accent }}>
                                    <h4 className="text-lg font-bold mb-2">{item.back_title || item.front_title}</h4>
                                    <p className="text-sm text-white/90 leading-relaxed">{item.back_text}</p>
                                    {item.back_cta && (
                                        <a {...resolveLinkProps(item.back_link || '#contact')} className="mt-4 px-5 py-2 rounded-lg bg-white/20 text-sm font-semibold hover:bg-white/30 transition-colors">
                                            {item.back_cta}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}

/* ── TiltCard sub-component for tilt-3d variant ── */
function TiltCard({ item, i, s, visible }) {
    const [transform, setTransform] = useState('perspective(800px) rotateX(0deg) rotateY(0deg)');
    const [glowPos, setGlowPos] = useState({ x: 50, y: 50 });
    const [hovering, setHovering] = useState(false);

    const handleMove = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const tiltX = (y - 0.5) * -20;
        const tiltY = (x - 0.5) * 20;
        setTransform(`perspective(800px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`);
        setGlowPos({ x: x * 100, y: y * 100 });
    }, []);

    const handleLeave = useCallback(() => {
        setTransform('perspective(800px) rotateX(0deg) rotateY(0deg)');
        setHovering(false);
    }, []);

    return (
        <div
            className={`relative rounded-2xl p-6 text-center shadow-lg overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{
                transitionDelay: visible ? `${150 + i * 100}ms` : '0ms',
                transform,
                backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f9fafb',
                transitionProperty: 'opacity, transform',
                transitionTimingFunction: 'ease-out',
            }}
            onMouseMove={handleMove}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={handleLeave}
        >
            {/* Glow effect */}
            <div className="absolute inset-0 opacity-0 transition-opacity duration-300 pointer-events-none"
                style={{
                    opacity: hovering ? 0.15 : 0,
                    background: `radial-gradient(circle at ${glowPos.x}% ${glowPos.y}%, ${s.accent}, transparent 60%)`,
                }} />
            {/* Front content */}
            <span className="text-4xl mb-4 block relative z-10">{item.front_icon || '⭐'}</span>
            <h3 className="text-lg font-bold relative z-10" style={{ color: s.text }}>{item.front_title}</h3>
            {item.front_subtitle && <p className="text-sm mt-2 relative z-10" style={{ color: s.textSub }}>{item.front_subtitle}</p>}
            {/* Back content — slides up from bottom */}
            <div className="absolute bottom-0 left-0 right-0 rounded-b-2xl p-5 text-center text-white translate-y-full transition-transform duration-400 z-20"
                style={{
                    backgroundColor: s.accent,
                    transform: hovering ? 'translateY(0)' : 'translateY(100%)',
                }}>
                <h4 className="text-sm font-bold mb-1">{item.back_title || item.front_title}</h4>
                <p className="text-xs text-white/90 leading-relaxed">{item.back_text}</p>
                {item.back_cta && (
                    <a {...resolveLinkProps(item.back_link || '#contact')} className="inline-block mt-2 px-4 py-1.5 rounded-lg bg-white/20 text-xs font-semibold hover:bg-white/30 transition-colors">
                        {item.back_cta}
                    </a>
                )}
            </div>
        </div>
    );
}
